import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Button, Input, Label, Spinner, Modal, ModalHeader,
  ModalBody, ModalFooter, Badge,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const EMPTY_FORM = {
  name: "", company: "", serial_no: "", sim_card_number: "",default_username: "", default_password: "",
  ip_address: "", vpn_password: "", vpn_ip: "", vpn_name: "",
  iot_username: "", iot_password: "", parameter_username: "",
  parameter_password: "", pc_vpn: null, device_vpn: null,
  machine: "", image: "router_images\\router906.jpeg",
};

/* ── small reusable field row ── */
const Field = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "0.5px solid #f0f0f0", fontSize: 13 }}>
    <span style={{ color: "#74788d", fontWeight: 500 }}>{label}</span>
    <span style={{ color: "#343a40", textAlign: "right", maxWidth: "55%" }}>{value || "—"}</span>
  </div>
);

const RouterConfig = () => {
  const [machines, setMachines]       = useState([]);
  const [routers, setRouters]         = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  /* form state */
  const [formData, setFormData]       = useState(EMPTY_FORM);
  const [editingId, setEditingId]     = useState(null); // null = add mode

  /* delete confirm modal */
  const [deleteTarget, setDeleteTarget] = useState(null); // router object
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* form card visible */
  const [showForm, setShowForm] = useState(false);

  /* ── fetch helpers ── */
  const fetchCompany = async () => {
    try {
      const res = await fetch("https://api.hanoch.in/api/company");
      if (res.ok) setCompanyData(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchMachine = async () => {
    try {
      const res = await fetch("https://api.hanoch.in/api/machines");
      if (res.ok) {
        const d = await res.json();
        setMachines(d?.data || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchRouters = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://api.hanoch.in/api/router-systems/");
      setRouters(res?.results || res?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchCompany();
    fetchMachine();
    fetchRouters();
  }, []);

  document.title = "Router Configuration | OptiMize";

  /* ── input change ── */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ── open form for ADD ── */
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  /* ── open form for EDIT ── */
  const openEdit = (router) => {
    setFormData({
      name:                 router.name                 || "",
      company:              router.company              || "",
      serial_no:            router.serial_no            || "",
      sim_card_number:      router.sim_card_number      || "",
      default_username:     router.default_username     || "",
      default_password:     router.default_password     || "",
      ip_address:           router.ip_address           || "",
      vpn_password:         router.vpn_password         || "",
      vpn_ip:               router.vpn_ip               || "",
      vpn_name:             router.vpn_name             || "",
      iot_username:         router.iot_username         || "",
      iot_password:         router.iot_password         || "",
      parameter_username:   router.parameter_username   || "",
      parameter_password:   router.parameter_password   || "",
      machine:              router.machine              || "",
      image:                router.image               || EMPTY_FORM.image,
      pc_vpn:             null,   // file fields reset — user re-uploads if changing
      device_vpn:   null,
    });
    setEditingId(router.id);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  /* ── submit (add or edit) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        // skip null file fields on edit (don't overwrite existing file)
        if (formData[key] !== null) data.append(key, formData[key]);
      });

      if (editingId) {
        await axios.put(
          `https://api.hanoch.in/api/router-systems/${editingId}/`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await axios.post(
          "https://api.hanoch.in/api/router-systems/",
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      cancelForm();
      fetchRouters();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  /* ── delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`https://api.hanoch.in/api/router-systems/${deleteTarget.id}/`);
      setDeleteTarget(null);
      fetchRouters();
    } catch (e) { console.error(e); }
    finally { setDeleteLoading(false); }
  };

  /* ── form section ── */
  const renderForm = () => (
    <Card className="mb-4" style={{ border: editingId ? "1px solid #3b82f6" : undefined }}>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {editingId ? (
            <><i className="mdi mdi-pencil me-2 text-primary" />Edit Router</>
          ) : (
            <><i className="mdi mdi-plus-circle me-2 text-success" />Add New Router</>
          )}
        </h5>
        <button
          onClick={cancelForm}
          style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#74788d" }}
        >✕</button>
      </CardHeader>

      <CardBody>
        <form onSubmit={handleSubmit}>
          {/* Row 1 */}
          <Row className="g-3">
            <Col md={4}>
              <Label>Name <span className="text-danger">*</span></Label>
              <Input name="name" value={formData.name} onChange={handleChange} required />
            </Col>
            <Col md={4}>
              <Label>Company <span className="text-danger">*</span></Label>
              <select className="form-control" name="company" value={formData.company} onChange={handleChange} required>
                <option value="">Select Company</option>
                {companyData.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Col>
            <Col md={4}>
              <Label>Machine</Label>
              <select className="form-control" name="machine" value={formData.machine} onChange={handleChange}>
                <option value="">Select Machine</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Col>

            <Col md={4}>
              <Label>Serial No</Label>
              <Input name="serial_no" value={formData.serial_no} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>SIM Card Number</Label>
              <Input name="sim_card_number" value={formData.sim_card_number} onChange={handleChange} />
            </Col>
             <Col md={4}>
              <Label>Default Username</Label>
              <Input name="default_username" value={formData.default_username} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>Default Password</Label>
              <Input type="password" name="default_password" value={formData.default_password} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>IP Address</Label>
              <Input name="ip_address" value={formData.ip_address} onChange={handleChange} />
            </Col>

            <Col md={4}>
              <Label>Config IP</Label>
              <Input name="vpn_password" value={formData.vpn_password} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>VPN IP</Label>
              <Input name="vpn_ip" value={formData.vpn_ip} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>VPN Name</Label>
              <Input name="vpn_name" value={formData.vpn_name} onChange={handleChange} />
            </Col>

            <Col md={4}>
              <Label>IOT Username</Label>
              <Input name="iot_username" value={formData.iot_username} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>IOT Password</Label>
              <Input type="password" name="iot_password" value={formData.iot_password} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>Parameter Username</Label>
              <Input name="parameter_username" value={formData.parameter_username} onChange={handleChange} />
            </Col>

            <Col md={4}>
              <Label>Parameter Password</Label>
              <Input type="password" name="parameter_password" value={formData.parameter_password} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Label>
               PC VPN {editingId && <span className="text-muted" style={{ fontSize: 11 }}>(leave blank to keep existing)</span>}
              </Label>
              <Input
                type="file"
                name="pc_vpn"
                onChange={(e) => setFormData({ ...formData, pc_vpn: e.target.files[0] || null })}
              />
            </Col>
            <Col md={4}>
              <Label>
                Device VPN {editingId && <span className="text-muted" style={{ fontSize: 11 }}>(leave blank to keep existing)</span>}
              </Label>
              <Input
                type="file"
                name="device_vpn"
                onChange={(e) => setFormData({ ...formData, device_vpn: e.target.files[0] || null })}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button type="submit" color={editingId ? "primary" : "success"} disabled={submitting}>
              {submitting && <Spinner size="sm" className="me-2" />}
              {editingId ? "Update Router" : "Save Router"}
            </Button>
            <Button type="button" color="secondary" outline onClick={cancelForm}>
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Router Configuration" />

          {/* Top action bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              {routers.length} router{routers.length !== 1 ? "s" : ""} configured
            </p>
            {!showForm && (
              <Button color="success" size="sm" onClick={openAdd}>
                <i className="mdi mdi-plus me-1" /> Add Router
              </Button>
            )}
          </div>

          {/* Form (shown when adding or editing) */}
          {showForm && renderForm()}

          {/* Router Cards */}
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Configured Routers</h5>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                  <p className="mt-2 text-muted">Loading routers…</p>
                </div>
              ) : routers.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="mdi mdi-router-wireless" style={{ fontSize: 48, opacity: 0.3 }} />
                  <p className="mt-2">No routers configured yet.</p>
                </div>
              ) : (
                <Row className="g-3">
                  {routers.map((router) => {
                    const machine = machines.find((m) => m.id === router.machine);
                    const isEditing = editingId === router.id;

                    return (
                      <Col xl={4} md={6} key={router.id}>
                        <Card
                          className="h-100 shadow-sm"
                          style={{
                            border: isEditing ? "1.5px solid #3b82f6" : "1px solid #e9ecef",
                            transition: "box-shadow .2s",
                          }}
                        >
                          {/* Card header */}
                          <CardHeader
                            className="d-flex justify-content-between align-items-center"
                            style={{ background: isEditing ? "#eff6ff" : undefined }}
                          >
                            <div>
                              <strong style={{ fontSize: 14 }}>{router.name}</strong>
                              {isEditing && (
                                <Badge color="primary" className="ms-2" style={{ fontSize: 10 }}>Editing</Badge>
                              )}
                              <div style={{ fontSize: 11, color: "#74788d", marginTop: 2 }}>
                                {router.serial_no || "No serial"}
                              </div>
                            </div>

                            {/* Action buttons */}
                            <div className="d-flex gap-1">
                              <button
                                title="Edit"
                                onClick={() => openEdit(router)}
                                style={{
                                  background: "none", border: "1px solid #dee2e6",
                                  borderRadius: 6, padding: "4px 8px",
                                  cursor: "pointer", color: "#3b82f6", fontSize: 13,
                                }}
                              >
                                <i className="mdi mdi-pencil" />
                              </button>
                              <button
                                title="Delete"
                                onClick={() => setDeleteTarget(router)}
                                style={{
                                  background: "none", border: "1px solid #dee2e6",
                                  borderRadius: 6, padding: "4px 8px",
                                  cursor: "pointer", color: "#ef4444", fontSize: 13,
                                }}
                              >
                                <i className="mdi mdi-delete" />
                              </button>
                            </div>
                          </CardHeader>

                          <CardBody style={{ padding: "14px 16px" }}>
                            {/* Router image */}
                            {router.image && (
                              <div className="text-center mb-3">
                                <img
                                  src={`https://api.hanoch.in/media/${router.image}`}
                                  alt="router"
                                  height="60"
                                  className="img-fluid"
                                  style={{ objectFit: "contain" }}
                                />
                              </div>
                            )}

                            {/* Fields */}
                            <Field label="Company"          value={router.company_name} />
                            <Field label="Machine"          value={machine?.name} />
                             <Field label="Default username"  value={router.defult_username} />
                            <Field label="Default Password"    value={router.default_password ? router.default_password : "—"} />
                            <Field label="IP Address"       value={router.ip_address} />
                            <Field label="VPN Password"     value={router.vpn_password ? router.vpn_password : "—"} />
                            <Field label="VPN IP"           value={router.vpn_ip} />
                            <Field label="VPN Name"         value={router.vpn_name} />
                            <Field label="SIM Card"         value={router.sim_card_number} />
                            <Field label="IOT User"         value={router.iot_username} />
                            <Field label="IOT Password"     value={router.iot_password ? router.iot_password: "—"} />
                            <Field label="Param User"       value={router.parameter_username} />
                            <Field label="Param Password"   value={router.parameter_password ? router.parameter_password : "—"} />

                            {/* File downloads */}
                            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                              {router.pc_vpn && (
                                <a
                                  href={router.pc_vpn}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  style={{
                                    flex: 1, textAlign: "center", fontSize: 12,
                                    padding: "5px 0", borderRadius: 6,
                                    border: "1px solid #dee2e6", color: "#3b82f6",
                                    textDecoration: "none",
                                  }}
                                >
                                  <i className="mdi mdi-download me-1" />VPN File
                                </a>
                              )}
                              {router.device_vpn && (
                                <a
                                  href={router.device_vpn}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  style={{
                                    flex: 1, textAlign: "center", fontSize: 12,
                                    padding: "5px 0", borderRadius: 6,
                                    border: "1px solid #dee2e6", color: "#6366f1",
                                    textDecoration: "none",
                                  }}
                                >
                                  <i className="mdi mdi-download me-1" />Config File
                                </a>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </CardBody>
          </Card>

          {/* ── Delete Confirm Modal ── */}
          <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered size="sm">
            <ModalHeader toggle={() => setDeleteTarget(null)}>
              <i className="mdi mdi-alert-circle text-danger me-2" />
              Delete Router
            </ModalHeader>
            <ModalBody>
              <p style={{ margin: 0, fontSize: 14 }}>
                Are you sure you want to delete{" "}
                <strong>{deleteTarget?.name}</strong>?
                <br />
                <span style={{ fontSize: 12, color: "#74788d" }}>This action cannot be undone.</span>
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading && <Spinner size="sm" className="me-1" />}
                Delete
              </Button>
              <Button color="secondary" outline onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default RouterConfig;